import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { listJobs, patchJob, type JobRow, type ScheduleConfig } from '../../api/admin-jobs'
import { LegendInfoIcon } from '../../components/primitives/LegendInfoIcon'
import { useToast } from '../../components/primitives/Toast'

// ── Friendly frequency <-> ScheduleConfig mapping ────────────────────────────
type Freq = 'hourly' | 'daily' | 'weekly' | 'fortnightly' | 'monthly' | 'yearly'

const FREQ_LABELS: ReadonlyArray<{ value: Freq; label: string }> = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
]
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

function freqOf(s: ScheduleConfig): Freq {
  if (s.unit === 'hour') return 'hourly'
  if (s.unit === 'day') return 'daily'
  if (s.unit === 'month') return 'monthly'
  if (s.unit === 'year') return 'yearly'
  return s.value >= 2 ? 'fortnightly' : 'weekly'
}

function toConfig(
  freq: Freq,
  dayOfWeek: number,
  dayOfMonth: number,
  month: number,
  hour: number,
): ScheduleConfig {
  switch (freq) {
    case 'hourly':
      return { unit: 'hour', value: 1, dayOfWeek: null, dayOfMonth: null, month: null, hour: 0 }
    case 'daily':
      return { unit: 'day', value: 1, dayOfWeek: null, dayOfMonth: null, month: null, hour }
    case 'weekly':
      return { unit: 'week', value: 1, dayOfWeek, dayOfMonth: null, month: null, hour }
    case 'fortnightly':
      return { unit: 'week', value: 2, dayOfWeek, dayOfMonth: null, month: null, hour }
    case 'monthly':
      return { unit: 'month', value: 1, dayOfWeek: null, dayOfMonth, month: null, hour }
    case 'yearly':
      return { unit: 'year', value: 1, dayOfWeek: null, dayOfMonth, month, hour }
  }
}

function sameConfig(a: ScheduleConfig, b: ScheduleConfig): boolean {
  return (
    a.unit === b.unit &&
    a.value === b.value &&
    a.dayOfWeek === b.dayOfWeek &&
    a.dayOfMonth === b.dayOfMonth &&
    a.month === b.month &&
    a.hour === b.hour
  )
}

const selStyle = {
  fontSize: 12,
  padding: '4px 8px',
  minHeight: 32,
  borderRadius: 6,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text)',
} as const

/** Schedule editor for one schedulable cron. Seeds from the job's current
 *  config; Save sends the assembled schedule. Disabled while the job is
 *  paused or a save is in flight. */
function SchedulePicker({
  config,
  disabled,
  saving,
  onSave,
}: {
  config: ScheduleConfig
  disabled: boolean
  saving: boolean
  onSave: (s: ScheduleConfig) => void
}): JSX.Element {
  const [freq, setFreq] = useState<Freq>(freqOf(config))
  const [dayOfWeek, setDayOfWeek] = useState(config.dayOfWeek ?? 0)
  const [dayOfMonth, setDayOfMonth] = useState(config.dayOfMonth ?? 1)
  const [month, setMonth] = useState(config.month ?? 1)
  const [hour, setHour] = useState(config.hour)

  // Re-seed when the server value changes (after a save / refetch).
  useEffect(() => {
    setFreq(freqOf(config))
    setDayOfWeek(config.dayOfWeek ?? 0)
    setDayOfMonth(config.dayOfMonth ?? 1)
    setMonth(config.month ?? 1)
    setHour(config.hour)
  }, [config])

  const draft = toConfig(freq, dayOfWeek, dayOfMonth, month, hour)
  const dirty = !sameConfig(draft, config)
  const showDay = freq === 'weekly' || freq === 'fortnightly'
  const showMonth = freq === 'yearly'
  const showDom = freq === 'monthly' || freq === 'yearly'
  const showHour = freq !== 'hourly'
  // Yearly allows up to 31 (e.g. Dec 31); monthly caps at 28 so it exists in
  // every month.
  const domMax = freq === 'yearly' ? 31 : 28

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
      <select aria-label="Frequency" value={freq} disabled={disabled} style={selStyle}
        onChange={(e) => setFreq(e.target.value as Freq)}>
        {FREQ_LABELS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
      </select>
      {showDay ? (
        <select aria-label="Day of week" value={dayOfWeek} disabled={disabled} style={selStyle}
          onChange={(e) => setDayOfWeek(Number(e.target.value))}>
          {DOW.map((d, i) => <option key={d} value={i}>{d}</option>)}
        </select>
      ) : null}
      {showMonth ? (
        <select aria-label="Month" value={month} disabled={disabled} style={selStyle}
          onChange={(e) => setMonth(Number(e.target.value))}>
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
      ) : null}
      {showDom ? (
        <select aria-label="Day of month" value={dayOfMonth} disabled={disabled} style={selStyle}
          onChange={(e) => setDayOfMonth(Number(e.target.value))}>
          {Array.from({ length: domMax }, (_, i) => i + 1).map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
      ) : null}
      {showHour ? (
        <select aria-label="Hour" value={hour} disabled={disabled} style={selStyle}
          onChange={(e) => setHour(Number(e.target.value))}>
          {Array.from({ length: 24 }, (_, i) => i).map((h) => (
            <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
          ))}
        </select>
      ) : null}
      <button
        type="button"
        disabled={disabled || saving || !dirty}
        onClick={() => onSave(draft)}
        style={{
          fontSize: 12,
          fontWeight: 600,
          padding: '6px 12px',
          minHeight: 32,
          borderRadius: 6,
          border: 'none',
          cursor: disabled || saving || !dirty ? 'default' : 'pointer',
          background: dirty && !disabled ? 'var(--ink)' : 'var(--french-gray)',
          color: dirty && !disabled ? 'var(--surface)' : 'var(--text-3)',
        }}
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  )
}

function fmtLastRun(iso: string | null): string {
  if (!iso) return 'never'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

/** Superadmin console for managed background jobs (arq crons + Slack
 *  notifications). Pause/resume any job; schedule the schedulable crons
 *  independently. The deployment-level env kill switches override these. */
export function AdminBackgroundJobsTab(): JSX.Element {
  const queryClient = useQueryClient()
  const { notify } = useToast()
  const jobs = useQuery({ queryKey: ['admin', 'jobs'], queryFn: listJobs })

  const mutation = useMutation({
    mutationFn: ({ name, input }: { name: string; input: { enabled: boolean; schedule?: ScheduleConfig } }) =>
      patchJob(name, input),
    onSuccess: (row) => {
      notify(`${row.label} updated`, 'success')
      void queryClient.invalidateQueries({ queryKey: ['admin', 'jobs'] })
    },
    onError: () => notify('Could not update the job. Try again.', 'error'),
  })

  if (jobs.isLoading) return <p style={{ color: 'var(--text-2)' }}>Loading…</p>
  if (jobs.isError || !jobs.data) {
    return <p style={{ color: 'var(--danger-fg)' }}>Could not load background jobs.</p>
  }

  const crons = jobs.data.filter((j) => j.kind === 'cron')
  const notifications = jobs.data.filter((j) => j.kind === 'notification')
  const busy = mutation.isPending ? (mutation.variables?.name ?? null) : null

  const renderRow = (job: JobRow): JSX.Element => {
    const rowBusy = busy === job.name
    return (
      <div
        key={job.name}
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: 12,
          alignItems: 'start',
          padding: '12px 0',
          borderBottom: '1px solid var(--line-subtle)',
          opacity: job.enabled ? 1 : 0.6,
          transition: 'opacity 120ms ease',
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6 }}>
            {job.label}
            <LegendInfoIcon tooltip={job.details} ariaLabel={`What ${job.label} does`} />
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-3)', margin: '2px 0 0' }}>{job.description}</p>
          <p style={{ fontSize: 12, color: 'var(--text-3)', margin: '4px 0 0' }}>
            {job.schedulable ? 'Default: ' : ''}{job.schedule}
            {job.kind === 'cron' ? ` · last run ${fmtLastRun(job.lastRunAt)}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, minHeight: 24 }}>
            <input
              type="checkbox"
              checked={job.enabled}
              disabled={rowBusy}
              onChange={(e) =>
                mutation.mutate({ name: job.name, input: { enabled: e.target.checked } })
              }
            />
            {job.enabled ? 'Active' : 'Paused'}
          </label>
          {job.schedulable && job.scheduleConfig ? (
            <SchedulePicker
              config={job.scheduleConfig}
              disabled={!job.enabled}
              saving={rowBusy}
              onSave={(schedule) =>
                mutation.mutate({ name: job.name, input: { enabled: job.enabled, schedule } })
              }
            />
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'grid', gap: 28, maxWidth: 760 }}>
      <p style={{ fontSize: 13, color: 'var(--text-2)', margin: 0 }}>
        Pause or resume background jobs and digest notifications, and set when the
        schedulable ones run, each on its own schedule. A change takes effect at
        that job's next scheduled run (within the hour for the hourly and
        schedulable jobs; at the next due date for the date-anchored ones). The
        deployment-level kill switches still override anything set here.
      </p>

      <section>
        <h3 style={{ font: '600 14px var(--font-sans)', color: 'var(--text)', margin: '0 0 4px' }}>
          Scheduled jobs ({crons.length})
        </h3>
        {crons.map(renderRow)}
      </section>

      <section>
        <h3 style={{ font: '600 14px var(--font-sans)', color: 'var(--text)', margin: '0 0 4px' }}>
          Digest notifications ({notifications.length})
        </h3>
        {notifications.map(renderRow)}
      </section>
    </div>
  )
}
